import { useEffect, useState, useRef } from "react";
import DashNav from "@/components/chalann/DashNav";
import { ManualTabPanel } from "@/components/chalann/ManualTabPanel";
import { isOnePay } from "@/lib/api";
import { motion, AnimatePresence } from "framer-motion";
import {
  Mic, Send, Beer, Plus, X, Volume2, VolumeX, Receipt, Loader2,
  Trash2, ChevronRight, Users, Sparkles, CheckCheck, Eye,
  ChefHat, MessageSquare, Utensils, Save, Pencil,
} from "lucide-react";
import { toast, Toaster } from "sonner";
import {
  listTabs, openTab, closeTab, deleteTab, tabVoiceCommand,
  adoptCategory, formatMXN, normalizeTTS,
  getKitchenWhatsApp, setKitchenWhatsApp, sendOrderManual,
  listProducts, createProduct,
} from "@/lib/api";

const EXAMPLES = [
  "Chalán, abre cuenta para Julio",
  "Envía comanda de alitas sabor búfalo a la mesa de Julio",
  "Agrega dos coronas a la cuenta de Julio",
  "Cierra la cuenta de Julio y dame el total",
];

const REPLY_MODE_KEY = "chalann_mesas_reply_mode"; // "voice" | "text"

function playSpeech(text) {
  if (!text || !("speechSynthesis" in window)) return;
  try { window.speechSynthesis.cancel(); } catch { /* noop */ }
  normalizeTTS(text).then((n) => {
    const clean = n?.text || text;
    const u = new SpeechSynthesisUtterance(clean);
    u.lang = "es-MX";
    u.rate = 1.05;
    window.speechSynthesis.speak(u);
  }).catch(() => {
    const u = new SpeechSynthesisUtterance(text);
    u.lang = "es-MX"; u.rate = 1.05;
    window.speechSynthesis.speak(u);
  });
}

export default function Mesas() {
  const [tabs, setTabs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [lastReply, setLastReply] = useState(null);
  const [selectedTab, setSelectedTab] = useState(null);
  const [showManual, setShowManual] = useState(false);
  const [manualName, setManualName] = useState("");
  const [listening, setListening] = useState(false);
  const [adopting, setAdopting] = useState(false);
  const [replyMode, setReplyMode] = useState(() => localStorage.getItem(REPLY_MODE_KEY) || "voice");
  // Kitchen WhatsApp config
  const [kitchenWa, setKitchenWa] = useState("");
  const [kitchenInput, setKitchenInput] = useState("");
  const [editingKitchen, setEditingKitchen] = useState(false);
  const [savingKitchen, setSavingKitchen] = useState(false);
  // Menu of platillos (products with category=Platillos)
  const [platillos, setPlatillos] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const onePay = isOnePay();
  const [showPlatilloForm, setShowPlatilloForm] = useState(false);
  const [platName, setPlatName] = useState("");
  const [platPrice, setPlatPrice] = useState("");
  const [savingPlatillo, setSavingPlatillo] = useState(false);
  const recognitionRef = useRef(null);

  useEffect(() => { localStorage.setItem(REPLY_MODE_KEY, replyMode); }, [replyMode]);

  const loadKitchen = () => getKitchenWhatsApp().then((r) => {
    setKitchenWa(r.number || "");
    setKitchenInput(r.number || "");
  }).catch(() => {});

  const loadPlatillos = () => listProducts().then((rows) => {
    setAllProducts(rows || []);
    setPlatillos((rows || []).filter((p) => (p.category || "").toLowerCase() === "platillos"));
  }).catch(() => {});

  useEffect(() => { loadKitchen(); loadPlatillos(); }, []);

  const refresh = () =>
    listTabs("open").then((r) => setTabs(r.tabs || [])).catch(() => {}).finally(() => setLoading(false));

  useEffect(() => { refresh(); }, []);

  const submit = async (cmdText, opts = {}) => {
    const t = (cmdText ?? text).trim();
    if (!t || busy) return;
    setBusy(true);
    try {
      const r = await tabVoiceCommand(t);
      setLastReply(r);
      const shouldSpeak = opts.forceSilent ? false : opts.forceSpeak ? true : replyMode === "voice";
      if (r.tts_message && shouldSpeak) playSpeech(r.tts_message);
      if (r.intent !== "none") setText("");
      // Auto-open WhatsApp for send_order responses (kitchen comanda)
      if (r.intent === "send_order" && r.whatsapp_url) {
        window.open(r.whatsapp_url, "_blank", "noopener,noreferrer");
        toast.success("Comanda enviada a cocina por WhatsApp");
      }
      if (r.intent === "send_order" && !r.kitchen_configured) {
        toast.error("Configura primero el WhatsApp de la cocina, arriba a la derecha");
        setEditingKitchen(true);
      }
      // If a tab appears/updates in response, refresh
      if (["open_tab", "add_to_tab", "close_tab", "send_order"].includes(r.intent)) {
        refresh();
        if (r.tab && r.intent !== "close_tab") setSelectedTab(r.tab);
      }
      // For describe_tab, keep drawer open with fresh data if user was viewing it
      if (r.intent === "describe_tab" && r.tab && selectedTab && selectedTab.id === r.tab.id) {
        setSelectedTab(r.tab);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No pudimos procesar el comando");
    } finally {
      setBusy(false);
    }
  };

  const saveKitchen = async () => {
    if (savingKitchen) return;
    setSavingKitchen(true);
    try {
      const r = await setKitchenWhatsApp(kitchenInput.trim());
      setKitchenWa(r.number || "");
      setEditingKitchen(false);
      toast.success(r.number ? "WhatsApp de la cocina guardado" : "WhatsApp de la cocina borrado");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo guardar");
    } finally {
      setSavingKitchen(false);
    }
  };

  const savePlatillo = async () => {
    const n = platName.trim();
    const p = parseFloat(platPrice);
    if (!n || !p || p <= 0) { toast.error("Nombre y precio válido"); return; }
    setSavingPlatillo(true);
    try {
      await createProduct({
        name: n, category: "Platillos", sale_price: p,
        cost_price: 0, stock_quantity: 999, min_stock_alert: 0, unit: "pieza",
      });
      toast.success(`Platillo agregado · ${n}`);
      setPlatName(""); setPlatPrice("");
      loadPlatillos();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo guardar el platillo");
    } finally {
      setSavingPlatillo(false);
    }
  };

  const sendComandaFromMenu = async (dishName, customerName) => {
    if (!customerName) { toast.error("Escribe la mesa/cliente"); return; }
    try {
      const r = await sendOrderManual(customerName, dishName);
      setLastReply(r);
      if (r.tts_message && replyMode === "voice") playSpeech(r.tts_message);
      if (r.whatsapp_url) {
        window.open(r.whatsapp_url, "_blank", "noopener,noreferrer");
        toast.success("Comanda enviada a cocina");
      } else if (!r.kitchen_configured) {
        toast.error("Configura primero el WhatsApp de la cocina");
        setEditingKitchen(true);
      }
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo enviar la comanda");
    }
  };

  const startMic = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { toast.error("Tu navegador no soporta micrófono"); return; }
    if (listening) return;
    const rec = new SR();
    rec.lang = "es-MX"; rec.continuous = false; rec.interimResults = true;
    let final = "";
    rec.onresult = (ev) => {
      for (let i = ev.resultIndex; i < ev.results.length; i++) {
        const r = ev.results[i];
        if (r.isFinal) final += r[0].transcript;
        else setText((prev) => (prev ? prev.split("…")[0] : "") + "…" + r[0].transcript);
      }
    };
    rec.onend = () => {
      setListening(false);
      if (final) { setText(final); submit(final); }
    };
    rec.onerror = () => { setListening(false); toast.error("Error del micrófono"); };
    recognitionRef.current = rec;
    setListening(true);
    try { rec.start(); } catch { setListening(false); }
  };

  const stopMic = () => {
    if (recognitionRef.current) { try { recognitionRef.current.stop(); } catch { /* noop */ } }
    setListening(false);
  };

  const doAdoptBeers = async () => {
    if (adopting) return;
    setAdopting(true);
    try {
      const r = await adoptCategory("Bebidas Alcohólicas");
      toast.success(`Se cargaron ${r.created || 0} cervezas. Edita los precios en Inventario.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo cargar el catálogo de cervezas");
    } finally { setAdopting(false); }
  };

  const openManual = async () => {
    const name = manualName.trim();
    if (!name) return;
    try {
      const r = await openTab(name);
      toast.success(r.reopened ? `Ya había cuenta abierta para ${name}` : `Cuenta abierta para ${name}`);
      setManualName(""); setShowManual(false);
      refresh();
      if (r.tab) setSelectedTab(r.tab);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo abrir la cuenta");
    }
  };

  const doClose = async (id) => {
    try {
      const r = await closeTab(id);
      if (r.tts_message && replyMode === "voice") playSpeech(r.tts_message);
      toast.success(`Cuenta cerrada · ${formatMXN(r.tab?.total || 0)}`);
      setSelectedTab(null);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo cerrar");
    }
  };

  const doDelete = async (id) => {
    if (!window.confirm("¿Borrar esta cuenta cerrada?")) return;
    try { await deleteTab(id); refresh(); } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo borrar");
    }
  };

  const totalOpen = tabs.reduce((s, t) => s + (Number(t.total) || 0), 0);

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <DashNav />

      <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-8">
        {/* Header */}
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <p className="font-mono text-[10px] tracking-widest text-white/40 uppercase mb-2">
              / Mesas · Cuentas por cliente
            </p>
            <h1 className="font-satoshi text-4xl md:text-5xl leading-none">
              Cuentas <span className="text-[#CCFF00]">abiertas</span>
            </h1>
            <p className="text-white/50 text-sm mt-2 max-w-xl">
              {onePay
                ? "Abre una cuenta por mesa o cliente y agrega productos tocándolos en el panel. Cada cliente lleva su cuenta hasta que la cierres."
                : <>Dile a Chalán {'"abre cuenta para Julio"'} y agrega bebidas por voz. Cada cliente lleva su cuenta hasta que la cierres.</>}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="rounded-full border border-white/10 bg-white/[0.03] px-4 py-2 text-xs font-mono uppercase tracking-widest text-white/60" data-testid="mesas-open-count">
              <Users size={13} className="inline mr-2 text-[#CCFF00]" />
              {tabs.length} abiertas · {formatMXN(totalOpen)}
            </div>
            <button
              onClick={doAdoptBeers}
              disabled={adopting}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#FF5A00]/40 bg-[#FF5A00]/10 text-[#FF5A00] text-xs font-mono uppercase tracking-widest hover:bg-[#FF5A00]/20 transition disabled:opacity-40"
              data-testid="mesas-load-beers-btn"
              title="Copia todo el catálogo de cervezas del maestro a tu inventario. Luego editas precios."
            >
              {adopting ? <Loader2 size={13} className="animate-spin" /> : <Beer size={13} />}
              Cargar catálogo de cervezas
            </button>
            <button
              onClick={() => setShowManual(true)}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#CCFF00] text-black text-xs font-mono uppercase tracking-widest hover:bg-white transition"
              data-testid="mesas-new-tab-btn"
            >
              <Plus size={13} /> Abrir cuenta
            </button>
          </div>
        </div>

        {/* Kitchen WhatsApp + Menú platillos row */}
        <div className="grid md:grid-cols-2 gap-4 mb-6">
          {/* Kitchen WhatsApp config */}
          <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5" data-testid="mesas-kitchen-wa-card">
            <div className="flex items-center justify-between mb-3">
              <p className="font-mono text-[10px] tracking-widest text-white/50 uppercase flex items-center gap-2">
                <ChefHat size={12} className="text-[#25D366]" /> WhatsApp de la cocina
              </p>
              {kitchenWa && !editingKitchen && (
                <button
                  onClick={() => { setKitchenInput(kitchenWa); setEditingKitchen(true); }}
                  className="text-white/40 hover:text-white text-[10px] font-mono uppercase tracking-widest flex items-center gap-1"
                  data-testid="mesas-kitchen-wa-edit"
                >
                  <Pencil size={10} /> Editar
                </button>
              )}
            </div>
            {editingKitchen || !kitchenWa ? (
              <>
                <p className="text-white/50 text-xs mb-3">
                  Las comandas se envían por WhatsApp al número del cocinero.
                </p>
                <div className="flex items-center gap-2">
                  <span className="font-mono text-xs text-white/40">+52</span>
                  <input
                    value={kitchenInput}
                    onChange={(e) => setKitchenInput(e.target.value.replace(/\D/g, "").slice(0, 10))}
                    onKeyDown={(e) => e.key === "Enter" && saveKitchen()}
                    placeholder="10 dígitos"
                    className="flex-1 bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm focus:border-[#25D366]/60 focus:outline-none"
                    data-testid="mesas-kitchen-wa-input"
                    inputMode="numeric"
                  />
                  <button
                    onClick={saveKitchen}
                    disabled={savingKitchen}
                    className="px-4 py-2 rounded-lg bg-[#25D366] text-black font-mono text-[10px] uppercase tracking-widest hover:bg-white transition disabled:opacity-40"
                    data-testid="mesas-kitchen-wa-save"
                  >
                    {savingKitchen ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
                  </button>
                </div>
              </>
            ) : (
              <div className="flex items-center gap-3">
                <MessageSquare size={16} className="text-[#25D366]" />
                <p className="font-mono text-sm text-white" data-testid="mesas-kitchen-wa-value">+52 {kitchenWa}</p>
                <span className="ml-auto rounded-full bg-[#25D366]/10 border border-[#25D366]/40 text-[#25D366] px-2 py-0.5 text-[9px] font-mono uppercase tracking-widest">
                  Activo
                </span>
              </div>
            )}
          </div>

          {/* Menú de platillos */}
          <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5" data-testid="mesas-platillos-card">
            <div className="flex items-center justify-between mb-3">
              <p className="font-mono text-[10px] tracking-widest text-white/50 uppercase flex items-center gap-2">
                <Utensils size={12} className="text-[#CCFF00]" /> Menú de platillos ({platillos.length})
              </p>
              <button
                onClick={() => setShowPlatilloForm((s) => !s)}
                className="text-[#CCFF00] hover:text-white text-[10px] font-mono uppercase tracking-widest flex items-center gap-1"
                data-testid="mesas-platillo-toggle-form"
              >
                {showPlatilloForm ? <X size={10} /> : <Plus size={10} />}
                {showPlatilloForm ? "Cerrar" : "Agregar"}
              </button>
            </div>

            {showPlatilloForm && (
              <div className="flex items-center gap-2 mb-3">
                <input
                  value={platName}
                  onChange={(e) => setPlatName(e.target.value)}
                  placeholder="Nombre (ej. Alitas Sabor Búfalo)"
                  className="flex-1 bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm focus:border-[#CCFF00]/50 focus:outline-none"
                  data-testid="mesas-platillo-name"
                />
                <div className="flex items-center border border-white/10 rounded-lg bg-black/40 px-3 py-2">
                  <span className="text-white/40 text-sm mr-1">$</span>
                  <input
                    value={platPrice}
                    onChange={(e) => setPlatPrice(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && savePlatillo()}
                    placeholder="180"
                    inputMode="decimal"
                    className="w-20 bg-transparent text-sm focus:outline-none"
                    data-testid="mesas-platillo-price"
                  />
                </div>
                <button
                  onClick={savePlatillo}
                  disabled={savingPlatillo || !platName.trim() || !platPrice}
                  className="px-3 py-2 rounded-lg bg-[#CCFF00] text-black font-mono text-[10px] uppercase tracking-widest hover:bg-white transition disabled:opacity-40"
                  data-testid="mesas-platillo-save"
                >
                  {savingPlatillo ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
                </button>
              </div>
            )}

            <div className="max-h-32 overflow-y-auto space-y-1">
              {platillos.length === 0 && !showPlatilloForm && (
                <p className="text-white/40 text-xs">
                  Agrega los platillos que sirves para que Chalán los pueda mandar a cocina.
                </p>
              )}
              {platillos.slice(0, 6).map((p) => (
                <div key={p.id} className="flex items-center justify-between text-xs py-1 border-b border-white/5 last:border-none" data-testid={`mesas-platillo-${p.id}`}>
                  <span className="text-white/70 truncate">{p.name}</span>
                  <span className="font-mono text-[#CCFF00]">{formatMXN(p.sale_price)}</span>
                </div>
              ))}
              {platillos.length > 6 && (
                <p className="text-white/30 text-[10px] font-mono uppercase tracking-widest pt-1">
                  + {platillos.length - 6} más — vívelos en Inventario
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-5 gap-6">
          {/* Voice panel (CHALANITO) · Manual panel (ONE PAY) */}
          <div className="lg:col-span-2">
            {onePay ? (
              <ManualTabPanel tabs={tabs} products={allProducts} onChanged={refresh} onOpenManual={() => setShowManual(true)} />
            ) : (
            <div className="sticky top-24 rounded-3xl border border-white/10 bg-gradient-to-br from-white/[0.03] to-white/[0.01] p-6" data-testid="mesas-voice-panel">
              <div className="flex items-center justify-between mb-3">
                <p className="font-mono text-[10px] tracking-widest text-white/40 uppercase flex items-center gap-2">
                  <Sparkles size={12} className="text-[#CCFF00]" /> Habla con Chalán
                </p>
                <button
                  onClick={() => setReplyMode((m) => (m === "voice" ? "text" : "voice"))}
                  className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[9px] font-mono uppercase tracking-widest transition ${
                    replyMode === "voice"
                      ? "border-[#CCFF00]/40 bg-[#CCFF00]/10 text-[#CCFF00]"
                      : "border-white/15 bg-white/[0.03] text-white/60 hover:text-white"
                  }`}
                  data-testid="mesas-reply-mode-toggle"
                  aria-pressed={replyMode === "voice"}
                  title={replyMode === "voice" ? "Chalán responde con voz. Toca para cambiar a solo pantalla." : "Chalán responde solo en pantalla. Toca para activar voz."}
                >
                  {replyMode === "voice" ? <Volume2 size={11} /> : <VolumeX size={11} />}
                  {replyMode === "voice" ? "Voz" : "Solo pantalla"}
                </button>
              </div>
              <div className="flex items-center gap-2 mb-4">
                <input
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && submit()}
                  placeholder="Ej. 'abre cuenta para Julio' o 'dos coronas a la cuenta de Ana'"
                  className="flex-1 bg-black/40 border border-white/10 rounded-full px-4 py-2.5 text-sm focus:border-[#CCFF00]/50 focus:outline-none"
                  disabled={busy}
                  data-testid="mesas-voice-input"
                />
                <button
                  onClick={listening ? stopMic : startMic}
                  className={`w-10 h-10 rounded-full flex items-center justify-center border transition ${
                    listening
                      ? "border-[#FF5A00] bg-[#FF5A00]/20 text-[#FF5A00] animate-pulse"
                      : "border-white/10 bg-white/[0.03] text-white/70 hover:text-white hover:border-white/30"
                  }`}
                  data-testid="mesas-mic-btn"
                  title={listening ? "Detener" : "Grabar"}
                >
                  <Mic size={16} />
                </button>
                <button
                  onClick={() => submit()}
                  disabled={busy || !text.trim()}
                  className="w-10 h-10 rounded-full flex items-center justify-center bg-[#CCFF00] text-black hover:bg-white transition disabled:opacity-40"
                  data-testid="mesas-send-btn"
                >
                  {busy ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                </button>
              </div>

              {/* Examples */}
              <div className="space-y-1.5 mb-4">
                <p className="font-mono text-[9px] tracking-widest text-white/30 uppercase">Ejemplos:</p>
                {EXAMPLES.map((ex) => (
                  <button
                    key={ex}
                    onClick={() => setText(ex)}
                    className="w-full text-left text-xs text-white/60 hover:text-white bg-white/[0.02] hover:bg-white/[0.05] border border-white/5 rounded-lg px-3 py-1.5 transition"
                  >
                    {ex}
                  </button>
                ))}
              </div>

              {/* Last reply */}
              {lastReply?.tts_message && (
                <motion.div
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="rounded-2xl border border-[#CCFF00]/30 bg-[#CCFF00]/[0.05] px-4 py-3"
                  data-testid="mesas-last-reply"
                >
                  <p className="font-mono text-[9px] tracking-widest text-[#CCFF00]/80 uppercase mb-1 flex items-center gap-1.5">
                    <Volume2 size={10} /> Chalán · {lastReply.intent}
                  </p>
                  <p className="text-white text-sm leading-relaxed">{lastReply.tts_message}</p>
                </motion.div>
              )}
            </div>
            )}
          </div>

          {/* Tabs list */}
          <div className="lg:col-span-3">
            {loading ? (
              <div className="rounded-3xl border border-dashed border-white/10 p-12 text-center">
                <Loader2 size={20} className="animate-spin mx-auto text-white/40" />
              </div>
            ) : tabs.length === 0 ? (
              <div className="rounded-3xl border border-dashed border-white/15 bg-white/[0.02] p-10 md:p-14 text-center" data-testid="mesas-empty">
                <Beer size={40} className="mx-auto mb-4 text-[#CCFF00]" />
                <h3 className="font-satoshi text-2xl mb-2">No hay cuentas abiertas</h3>
                <p className="text-white/50 text-sm max-w-md mx-auto">
                  {onePay
                    ? "Presiona Abrir cuenta arriba y agrega productos desde el panel. Antes, carga tu catálogo y ponle precio en Inventario."
                    : <>Dile a Chalán {'"abre cuenta para Julio"'} o presiona Abrir cuenta arriba. Antes, carga tu catálogo de cervezas y ponles precio en Inventario.</>}
                </p>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 gap-4" data-testid="mesas-tabs-grid">
                {tabs.map((t) => (
                  <TabCard
                    key={t.id}
                    tab={t}
                    onOpen={() => setSelectedTab(t)}
                    onClose={() => doClose(t.id)}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Manual open modal */}
      <AnimatePresence>
        {showManual && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-center justify-center p-4"
            onClick={() => setShowManual(false)}
            data-testid="mesas-open-modal"
          >
            <motion.div
              initial={{ scale: 0.95, y: 12 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 12 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-md rounded-3xl border border-white/10 bg-[#0a0a0a] p-6"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-satoshi text-xl">Abrir cuenta manual</h3>
                <button onClick={() => setShowManual(false)} className="text-white/40 hover:text-white" data-testid="mesas-open-modal-close">
                  <X size={18} />
                </button>
              </div>
              <label className="font-mono text-[10px] tracking-widest text-white/40 uppercase block mb-2">
                Nombre del cliente o mesa
              </label>
              <input
                autoFocus
                value={manualName}
                onChange={(e) => setManualName(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && openManual()}
                placeholder="Ej. Julio, Mesa 4, María Luisa"
                className="w-full bg-black/40 border border-white/10 rounded-full px-4 py-2.5 text-sm focus:border-[#CCFF00]/50 focus:outline-none mb-4"
                data-testid="mesas-open-modal-input"
              />
              <button
                onClick={openManual}
                disabled={!manualName.trim()}
                className="w-full py-3 rounded-full bg-[#CCFF00] text-black font-mono text-xs uppercase tracking-widest hover:bg-white transition disabled:opacity-40"
                data-testid="mesas-open-modal-submit"
              >
                Abrir cuenta
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Tab detail drawer */}
      <AnimatePresence>
        {selectedTab && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-end md:items-center justify-center p-0 md:p-4"
            onClick={() => setSelectedTab(null)}
            data-testid="mesas-tab-detail"
          >
            <motion.div
              initial={{ y: 60 }} animate={{ y: 0 }} exit={{ y: 60 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full md:max-w-lg rounded-t-3xl md:rounded-3xl border border-white/10 bg-[#0a0a0a] p-6 max-h-[85vh] overflow-y-auto"
            >
              <div className="flex items-start justify-between mb-4">
                <div>
                  <p className="font-mono text-[10px] tracking-widest text-[#CCFF00]/80 uppercase mb-1">
                    Cuenta {selectedTab.status === "open" ? "abierta" : "cerrada"}
                  </p>
                  <h3 className="font-satoshi text-2xl">{selectedTab.customer_name}</h3>
                </div>
                <button onClick={() => setSelectedTab(null)} className="text-white/40 hover:text-white" data-testid="mesas-tab-detail-close">
                  <X size={18} />
                </button>
              </div>

              <div className="space-y-2 mb-4">
                {(selectedTab.items || []).length === 0 && (
                  <p className="text-white/40 text-sm text-center py-6">
                    Sin bebidas todavía. Dile a Chalán {`"agrega dos coronas a la cuenta de ${selectedTab.customer_name}"`}.
                  </p>
                )}
                {(selectedTab.items || []).map((it, i) => (
                  <div key={i} className="flex items-baseline justify-between border-b border-white/5 py-2">
                    <div className="min-w-0">
                      <p className="text-white text-sm truncate">{it.name}</p>
                      <p className="font-mono text-[9px] text-white/40 uppercase tracking-widest">
                        {it.qty} × {formatMXN(it.unit_price)}
                      </p>
                    </div>
                    <p className="font-satoshi text-sm text-[#CCFF00]">{formatMXN(it.subtotal)}</p>
                  </div>
                ))}
              </div>

              <div className="flex items-baseline justify-between border-t border-white/15 pt-3 mb-5">
                <span className="font-mono text-[10px] tracking-widest uppercase text-white/60">Total</span>
                <span className="font-satoshi text-3xl text-white" data-testid="mesas-tab-detail-total">
                  {formatMXN(selectedTab.total)}
                </span>
              </div>

              {selectedTab.status === "open" ? (
                <>
                  <div className="grid grid-cols-2 gap-2 mb-2">
                    <button
                      onClick={() => submit(`qué lleva ${selectedTab.customer_name}`, { forceSilent: true })}
                      className="py-3 rounded-full border border-white/10 hover:border-[#CCFF00]/50 text-white/70 hover:text-[#CCFF00] font-mono text-xs uppercase tracking-widest transition"
                      data-testid="mesas-tab-detail-show"
                      title="Muestra el desglose en pantalla, sin audio"
                    >
                      <Eye size={12} className="inline mr-1.5" /> Mostrar
                    </button>
                    <button
                      onClick={() => submit(`qué lleva ${selectedTab.customer_name}`, { forceSpeak: true })}
                      className="py-3 rounded-full border border-white/10 hover:border-white/30 text-white/70 hover:text-white font-mono text-xs uppercase tracking-widest transition"
                      data-testid="mesas-tab-detail-speak"
                      title="Chalán lee las bebidas en voz alta"
                    >
                      <Volume2 size={12} className="inline mr-1.5" /> Leer
                    </button>
                  </div>
                  <button
                    onClick={() => doClose(selectedTab.id)}
                    className="w-full py-3 rounded-full bg-[#FF5A00] text-white font-mono text-xs uppercase tracking-widest hover:bg-white hover:text-black transition"
                    data-testid="mesas-tab-detail-close-btn"
                  >
                    <CheckCheck size={12} className="inline mr-2" /> Cerrar cuenta
                  </button>
                </>
              ) : (
                <button
                  onClick={() => doDelete(selectedTab.id)}
                  className="w-full py-3 rounded-full border border-white/10 hover:border-red-500/50 text-white/60 hover:text-red-400 font-mono text-xs uppercase tracking-widest transition flex items-center justify-center gap-2"
                >
                  <Trash2 size={12} /> Borrar cuenta cerrada
                </button>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      <Toaster theme="dark" position="top-center" />
    </div>
  );
}

function TabCard({ tab, onOpen, onClose }) {
  const items = tab.items || [];
  const preview = items.slice(0, 3);
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="relative rounded-2xl border border-white/10 bg-gradient-to-br from-white/[0.04] to-white/[0.01] p-5 hover:border-[#CCFF00]/30 transition cursor-pointer group"
      onClick={onOpen}
      data-testid={`mesas-tab-${tab.customer_key}`}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="min-w-0">
          <p className="font-mono text-[9px] tracking-widest text-white/40 uppercase mb-1">
            {items.length} bebida{items.length === 1 ? "" : "s"}
          </p>
          <h3 className="font-satoshi text-xl truncate group-hover:text-[#CCFF00] transition">
            {tab.customer_name}
          </h3>
        </div>
        <ChevronRight size={18} className="text-white/30 group-hover:text-white transition" />
      </div>

      <div className="space-y-1 mb-3 min-h-[52px]">
        {preview.length === 0 && (
          <p className="text-white/30 text-xs font-mono uppercase tracking-widest">· vacía ·</p>
        )}
        {preview.map((it, i) => (
          <p key={i} className="text-white/70 text-xs truncate">
            <span className="text-[#CCFF00] font-mono mr-2">{it.qty}×</span>{it.name}
          </p>
        ))}
        {items.length > 3 && (
          <p className="text-white/30 text-[10px] font-mono uppercase tracking-widest">
            + {items.length - 3} más
          </p>
        )}
      </div>

      <div className="flex items-baseline justify-between border-t border-white/5 pt-3">
        <span className="font-mono text-[9px] tracking-widest uppercase text-white/40">Total</span>
        <span className="font-satoshi text-xl text-white">{formatMXN(tab.total)}</span>
      </div>

      <button
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition text-white/40 hover:text-[#FF5A00]"
        title="Cobrar (cerrar cuenta)"
        data-testid={`mesas-tab-close-${tab.customer_key}`}
      >
        <Receipt size={14} />
      </button>
    </motion.div>
  );
}
